import { jsPDF } from "jspdf";
import {
  calculateSalaryBreakdown,
  formatLeaveCountForPayslip,
  getLeaveDaysTaken,
  getMonthLabel,
  getPaidLeaveUtilized,
  getTdsDisplayPercent,
  isTraineeDesignation,
  toNumber,
} from "./salaryCalculations";
import { readPayslipCompany } from "./payslipCompanyDetails";
import { getPayslipLetterheadKey, getPayslipLetterheadUrl } from "./payslipLetterhead";

/** Horizontal inset within the letterhead white content area */
const CONTENT_MARGIN_X = 48;
/** Below letterhead logo / header band */
const CONTENT_TOP = 142;
/** Above letterhead footer contact bar */
const CONTENT_BOTTOM_RESERVE = 92;
const RUPEE_SYMBOL = "\u20B9";
const GRAY = [211, 211, 211];
const BORDER = [0, 0, 0];
const TEXT = [0, 0, 0];

/** Render text with ₹ for jsPDF — default Helvetica lacks the rupee glyph. Size matches PDF pt. */
const textToImageDataUrl = (text, { fontSize = 9, bold = false, color = "#000000" } = {}) => {
  if (typeof document === "undefined") return null;
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  const canvasFontPx = Math.round(fontSize * (96 / 72));
  const font = `${bold ? "bold " : ""}${canvasFontPx}px Arial, Helvetica, sans-serif`;
  ctx.font = font;
  const width = Math.ceil(ctx.measureText(text).width) + 12;
  const height = canvasFontPx + 8;
  canvas.width = width;
  canvas.height = height;
  ctx.font = font;
  ctx.fillStyle = color;
  ctx.textBaseline = "middle";
  ctx.fillText(text, 6, height / 2);
  return {
    dataUrl: canvas.toDataURL("image/png"),
    width,
    height,
  };
};

const formatRs = (value) => {
  const amount = Math.round(toNumber(value));
  return `Rs. ${amount.toLocaleString("en-IN")}`;
};

const formatDaysLabel = (value) => {
  const n = toNumber(value);
  if (!n) return "0 days";
  const unit = n === 1 || n === 0.5 ? "day" : "days";
  return `${n} ${unit}`;
};

const getOrdinal = (day) => {
  const n = Number(day);
  if (n % 100 >= 11 && n % 100 <= 13) return `${n}th`;
  const suffix = ["th", "st", "nd", "rd"][n % 10 > 3 ? 0 : n % 10] || "th";
  return `${n}${suffix}`;
};

const formatDateOfJoining = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  const day = getOrdinal(date.getDate());
  const month = date.toLocaleDateString("en-IN", { month: "long" });
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
};

const formatEmployeeCode = (slip) => {
  const raw = String(slip.employeeCode || "").trim();
  if (raw) {
    return /^emp/i.test(raw) ? raw.toUpperCase() : `EMP${raw}`;
  }
  return `EMP${String(slip.userId || "").padStart(4, "0")}`;
};

const slipShowsEmployeeCode = (slip) => {
  const name = String(slip.employeeName || "").trim().toLowerCase();
  const hasCode = Boolean(String(slip.employeeCode || "").trim());
  return hasCode && (/\bpradip\b/.test(name) || /\btanish\b/.test(name));
};

const resolveEmployeeTypeField = (slip) => {
  if (slipShowsEmployeeCode(slip)) {
    return { employeeTypeLabel: "Employee Code", employeeType: formatEmployeeCode(slip) };
  }
  return { employeeTypeLabel: "Employment Type", employeeType: "Full time" };
};

const ONES = [
  "",
  "one",
  "two",
  "three",
  "four",
  "five",
  "six",
  "seven",
  "eight",
  "nine",
  "ten",
  "eleven",
  "twelve",
  "thirteen",
  "fourteen",
  "fifteen",
  "sixteen",
  "seventeen",
  "eighteen",
  "nineteen",
];
const TENS = ["", "", "twenty", "thirty", "forty", "fifty", "sixty", "seventy", "eighty", "ninety"];

const twoDigitWords = (n) => {
  if (n < 20) return ONES[n];
  const tens = Math.floor(n / 10);
  const ones = n % 10;
  return ones ? `${TENS[tens]} ${ONES[ones]}` : TENS[tens];
};

const threeDigitWords = (n) => {
  const hundred = Math.floor(n / 100);
  const rest = n % 100;
  if (!hundred) return twoDigitWords(rest);
  return rest ? `${ONES[hundred]} hundred ${twoDigitWords(rest)}` : `${ONES[hundred]} hundred`;
};

/** Indian numbering — rupees in words for payslip footer. */
export const amountToIndianWords = (amount) => {
  const rupees = Math.round(toNumber(amount));
  if (!rupees) return "rupees zero only";

  const parts = [];
  const crore = Math.floor(rupees / 10000000);
  const lakh = Math.floor((rupees % 10000000) / 100000);
  const thousand = Math.floor((rupees % 100000) / 1000);
  const hundred = rupees % 1000;

  if (crore) parts.push(`${threeDigitWords(crore)} crore`);
  if (lakh) parts.push(`${twoDigitWords(lakh)} lakh`);
  if (thousand) parts.push(`${twoDigitWords(thousand)} thousand`);
  if (hundred) parts.push(threeDigitWords(hundred));

  const words = parts.join(" ").replace(/\s+/g, " ").trim();
  return `rupees ${words} only`;
};

const buildSlipViewModel = (slip) => {
  const isTrainee = isTraineeDesignation(slip.designation);
  const breakdown = isTrainee ? null : calculateSalaryBreakdown(slip.monthlySalary);
  const leavesTaken = getLeaveDaysTaken(slip);
  const paidLeaveUtilized = getPaidLeaveUtilized(slip);
  const grossEarnings = toNumber(slip.grossEarnings);
  const tdsPercent = getTdsDisplayPercent(slip);
  const leaveDeduction = toNumber(slip.leaveDeduction);

  const earnings = isTrainee
    ? [{ label: "Stipend", amount: grossEarnings }]
    : [
      { label: "Basic", amount: breakdown.basicSalary },
      { label: "HRA", amount: breakdown.hra },
      { label: "Conveyance Allowance", amount: breakdown.conveyanceAllowance },
      { label: "Special Allowance", amount: breakdown.specialAllowance },
    ];

  const deductions = isTrainee
    ? leaveDeduction > 0
      ? [{ label: "Leave Deduction", amount: leaveDeduction }]
      : []
    : [
      { label: "Professional Tax", amount: slip.professionalTax },
      {
        label: tdsPercent
          ? `Tax deducted at source [${tdsPercent}%]`.replace(/ /g, "\u00A0")
          : "Tax\u00A0deducted\u00A0at\u00A0source",
        amount: slip.tds,
      },
      { label: "Leave Deduction", amount: leaveDeduction },
    ];

  return {
    periodTitle: `Pay Slip for the Month of ${getMonthLabel(slip.sheetMonth)} ${slip.sheetYear}`,
    employeeName: slip.employeeName || "—",
    ...resolveEmployeeTypeField(slip),
    designation: slip.designation || "—",
    dateOfJoining: formatDateOfJoining(slip.dateOfJoining),
    totalWorkingDays: formatDaysLabel(slip.totalWorkingDays),
    leavesTaken: formatLeaveCountForPayslip(leavesTaken),
    paidLeaveUtilized: formatLeaveCountForPayslip(paidLeaveUtilized),
    paidDays: formatDaysLabel(slip.workedDays),
    earnings,
    totalEarning: grossEarnings,
    deductions,
    totalDeduction: slip.totalDeduction,
    netPayable: slip.netSalary,
    netWords: amountToIndianWords(slip.netSalary),
  };
};

const drawBorderedCell = (pdf, x, y, w, h, {
  text = "",
  align = "left",
  bold = false,
  fill = null,
  fontSize = 9,
  padding = 6,
  imageDataUrl = null,
  imageMeta = null,
} = {}) => {
  pdf.setDrawColor(...BORDER);
  pdf.setLineWidth(0.5);
  if (fill) {
    pdf.setFillColor(...fill);
    pdf.rect(x, y, w, h, "FD");
  } else {
    pdf.rect(x, y, w, h);
  }
  if (imageDataUrl) {
    const targetH = bold ? fontSize + 2 : fontSize + 1;
    const aspect =
      imageMeta?.width && imageMeta?.height ? imageMeta.width / imageMeta.height : 4;
    const imgH = targetH;
    const imgW = imgH * aspect;
    let imgX = x + padding;
    if (align === "center") imgX = x + (w - imgW) / 2;
    if (align === "right") imgX = x + w - imgW - padding;
    const imgY = y + (h - imgH) / 2;
    try {
      pdf.addImage(imageDataUrl, "PNG", imgX, imgY, imgW, imgH);
    } catch {
      // fallback below
    }
    if (!text) return;
  }

  if (!text) return;

  pdf.setTextColor(...TEXT);
  pdf.setFont("helvetica", bold ? "bold" : "normal");
  pdf.setFontSize(fontSize);
  const maxTextWidth = w - padding * 2;
  const lines = pdf.splitTextToSize(String(text), maxTextWidth);
  const lineHeight = fontSize + 3;
  const blockHeight = lines.length * lineHeight;
  let textY = y + (h - blockHeight) / 2 + fontSize - 1;

  lines.forEach((line) => {
    let textX = x + padding;
    if (align === "center") {
      textX = x + w / 2;
    } else if (align === "right") {
      textX = x + w - padding;
    }
    pdf.text(line, textX, textY, { align });
    textY += lineHeight;
  });
};

const PAYSLIP_ROW_H = 22;
const PAYSLIP_HEADER_H = 24;
const PAYSLIP_CELL_PADDING = 6;
const PAYSLIP_BODY_FONT = 9;
const PAYSLIP_LABEL_FONT = 9;

/** Payslip table column widths (must sum to 1). */
const getPayslipTableColWidths = (tableWidth) => {
  const labelShare = 0.24;
  const valueShare = 0.26;
  const deductionsLabelShare = 0.3;
  const deductionAmountShare = 0.2;
  return [
    tableWidth * labelShare,
    tableWidth * valueShare,
    tableWidth * deductionsLabelShare,
    tableWidth * deductionAmountShare,
  ];
};

const payslipCell = (opts = {}) => ({
  fontSize: PAYSLIP_BODY_FONT,
  padding: PAYSLIP_CELL_PADDING,
  align: "left",
  ...opts,
});

const drawInfoGrid = (pdf, startX, startY, colWidths, rows) => {
  const [labelColW, valueColW, rightLabelColW, rightValueColW] = colWidths;
  let y = startY;

  rows.forEach(([leftLabel, leftValue, rightLabel, rightValue]) => {
    let x = startX;
    drawBorderedCell(
      pdf,
      x,
      y,
      labelColW,
      PAYSLIP_ROW_H,
      payslipCell({ text: leftLabel, bold: true, fontSize: PAYSLIP_LABEL_FONT, padding: 4 })
    );
    x += labelColW;
    drawBorderedCell(pdf, x, y, valueColW, PAYSLIP_ROW_H, payslipCell({ text: leftValue }));
    x += valueColW;
    drawBorderedCell(
      pdf,
      x,
      y,
      rightLabelColW,
      PAYSLIP_ROW_H,
      payslipCell({ text: rightLabel, bold: true, fontSize: PAYSLIP_LABEL_FONT, padding: 4 })
    );
    x += rightLabelColW;
    drawBorderedCell(pdf, x, y, rightValueColW, PAYSLIP_ROW_H, payslipCell({ text: rightValue }));
    y += PAYSLIP_ROW_H;
  });

  return y;
};

const drawEarningsDeductionsTable = (pdf, startX, startY, colWidths, model) => {
  const [earnLabelW, earnAmountW, deductLabelW, deductAmountW] = colWidths;
  let y = startY;

  const amountHeaderLabel = `Amount (${RUPEE_SYMBOL})`;
  const amountHeaderImage = textToImageDataUrl(amountHeaderLabel, {
    fontSize: PAYSLIP_BODY_FONT,
    bold: true,
  });
  const headers = [
    { text: "Earnings", width: earnLabelW },
    {
      text: amountHeaderLabel,
      width: earnAmountW,
      imageDataUrl: amountHeaderImage?.dataUrl,
      imageMeta: amountHeaderImage,
    },
    { text: "Deductions", width: deductLabelW },
    {
      text: amountHeaderLabel,
      width: deductAmountW,
      imageDataUrl: amountHeaderImage?.dataUrl,
      imageMeta: amountHeaderImage,
    },
  ];
  let x = startX;
  headers.forEach((header, columnIndex) => {
    drawBorderedCell(pdf, x, y, header.width, PAYSLIP_HEADER_H, {
      text: header.imageDataUrl ? "" : header.text,
      imageDataUrl: header.imageDataUrl,
      imageMeta: header.imageMeta,
      bold: true,
      fill: GRAY,
      align: "left",
      fontSize: PAYSLIP_BODY_FONT,
      padding: columnIndex === 0 || columnIndex === 2 ? 4 : PAYSLIP_CELL_PADDING,
    });
    x += header.width;
  });
  y += PAYSLIP_HEADER_H;

  const maxRows = Math.max(model.earnings.length, model.deductions.length);
  for (let i = 0; i < maxRows; i += 1) {
    const earning = model.earnings[i];
    const deduction = model.deductions[i];
    x = startX;
    drawBorderedCell(
      pdf,
      x,
      y,
      earnLabelW,
      PAYSLIP_ROW_H,
      payslipCell({ text: earning?.label || "", padding: 4 })
    );
    x += earnLabelW;
    drawBorderedCell(
      pdf,
      x,
      y,
      earnAmountW,
      PAYSLIP_ROW_H,
      payslipCell({ text: earning ? formatRs(earning.amount) : "" })
    );
    x += earnAmountW;
    drawBorderedCell(
      pdf,
      x,
      y,
      deductLabelW,
      PAYSLIP_ROW_H,
      payslipCell({ text: deduction?.label || "", padding: 4 })
    );
    x += deductLabelW;
    drawBorderedCell(
      pdf,
      x,
      y,
      deductAmountW,
      PAYSLIP_ROW_H,
      payslipCell({ text: deduction ? formatRs(deduction.amount) : "" })
    );
    y += PAYSLIP_ROW_H;
  }

  x = startX;
  drawBorderedCell(
    pdf,
    x,
    y,
    earnLabelW,
    PAYSLIP_ROW_H,
    payslipCell({ text: "Total Earning", bold: true, padding: 4 })
  );
  x += earnLabelW;
  drawBorderedCell(
    pdf,
    x,
    y,
    earnAmountW,
    PAYSLIP_ROW_H,
    payslipCell({ text: formatRs(model.totalEarning), bold: true })
  );
  x += earnAmountW;
  drawBorderedCell(
    pdf,
    x,
    y,
    deductLabelW,
    PAYSLIP_ROW_H,
    payslipCell({ text: "Total Deduction", bold: true, padding: 4 })
  );
  x += deductLabelW;
  drawBorderedCell(
    pdf,
    x,
    y,
    deductAmountW,
    PAYSLIP_ROW_H,
    payslipCell({ text: formatRs(model.totalDeduction), bold: true })
  );
  y += PAYSLIP_ROW_H;

  drawBorderedCell(pdf, startX, y, earnLabelW + earnAmountW, PAYSLIP_ROW_H, payslipCell({ text: "" }));
  drawBorderedCell(
    pdf,
    startX + earnLabelW + earnAmountW,
    y,
    deductLabelW,
    PAYSLIP_ROW_H,
    payslipCell({ text: "Net Amount Payable", bold: true, padding: 4 })
  );
  drawBorderedCell(
    pdf,
    startX + earnLabelW + earnAmountW + deductLabelW,
    y,
    deductAmountW,
    PAYSLIP_ROW_H,
    payslipCell({ text: formatRs(model.netPayable), bold: true })
  );
  y += PAYSLIP_ROW_H;

  drawBorderedCell(
    pdf,
    startX,
    y,
    earnLabelW,
    PAYSLIP_ROW_H,
    payslipCell({ text: "Net Amount in words", bold: true, padding: 4 })
  );
  drawBorderedCell(
    pdf,
    startX + earnLabelW,
    y,
    earnAmountW + deductLabelW + deductAmountW,
    PAYSLIP_ROW_H,
    payslipCell({ text: model.netWords })
  );
  y += PAYSLIP_ROW_H;

  return y;
};

const letterheadDataUrlCacheByKey = new Map();
const LETTERHEAD_EMAIL_MAX_WIDTH = 1200;

/** YYYY-MM slug for payslip file names (salary sheet year/month). */
export const formatPaySlipDateSlug = (slip) => {
  const y = Number(slip?.sheetYear);
  const m = Number(slip?.sheetMonth);
  if (y && m) {
    return `${y}-${String(m).padStart(2, "0")}`;
  }
  const paid = slip?.paidAt ? new Date(slip.paidAt) : null;
  if (paid && !Number.isNaN(paid.getTime())) {
    return `${paid.getFullYear()}-${String(paid.getMonth() + 1).padStart(2, "0")}`;
  }
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

const loadLetterheadDataUrl = (company, { forEmail = false } = {}) => {
  const cacheKey = `${getPayslipLetterheadKey(company?.companyName)}:${forEmail ? "email" : "full"}`;
  if (letterheadDataUrlCacheByKey.has(cacheKey)) {
    return Promise.resolve(letterheadDataUrlCacheByKey.get(cacheKey));
  }
  if (typeof document === "undefined") {
    return Promise.reject(new Error("Payslip PDF can only be generated in the browser."));
  }
  const letterheadUrl = getPayslipLetterheadUrl(company);
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      let width = img.naturalWidth;
      let height = img.naturalHeight;
      if (forEmail && width > LETTERHEAD_EMAIL_MAX_WIDTH) {
        const scale = LETTERHEAD_EMAIL_MAX_WIDTH / width;
        width = LETTERHEAD_EMAIL_MAX_WIDTH;
        height = Math.round(height * scale);
      }
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("Failed to prepare payslip letterhead."));
        return;
      }
      ctx.drawImage(img, 0, 0, width, height);
      const format = forEmail ? "JPEG" : "PNG";
      const dataUrl = forEmail
        ? canvas.toDataURL("image/jpeg", 0.82)
        : canvas.toDataURL("image/png");
      const payload = { dataUrl, format, forEmail };
      letterheadDataUrlCacheByKey.set(cacheKey, payload);
      resolve(payload);
    };
    img.onerror = () =>
      reject(new Error(`Failed to load payslip letterhead for ${company?.companyName || "company"}.`));
    img.src = letterheadUrl;
  });
};

const drawLetterheadBackground = (pdf, letterhead) => {
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const dataUrl = typeof letterhead === "string" ? letterhead : letterhead?.dataUrl;
  const format = typeof letterhead === "string" ? "PNG" : letterhead?.format || "PNG";
  const compress = letterhead?.forEmail ? "FAST" : undefined;
  pdf.addImage(dataUrl, format, 0, 0, pageWidth, pageHeight, undefined, compress);
};

const drawSignature = (pdf, startX, y, company) => {
  const ownerName = company?.ownerName || "";
  const companyName = company?.companyName || "";
  const signature = company?.signatureDataUrl;

  if (signature && String(signature).startsWith("data:image")) {
    try {
      const format = String(signature).includes("image/png") ? "PNG" : "JPEG";
      pdf.addImage(signature, format, startX, y, 90, 36);
      y += 42;
    } catch {
      y += 8;
    }
  } else {
    y += 44;
  }

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(10);
  pdf.setTextColor(...TEXT);
  if (ownerName) {
    const titled =
      /^(mr|ms|mrs|dr)\.?\s/i.test(ownerName) ? ownerName : `Mr. ${ownerName}`;
    pdf.text(titled, startX, y);
    y += 14;
  }
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9);
  pdf.text("Founder & CEO", startX, y);
  y += 12;
  if (companyName) {
    pdf.text(companyName, startX, y);
  }
};

export const getSalarySlipFileName = (slip) => `pay-slip-${formatPaySlipDateSlug(slip)}.pdf`;

export const MAX_SALARY_SLIP_EMAIL_BYTES = 8 * 1024 * 1024;

/**
 * Build a salary slip PDF on the company letterhead (does not download).
 */
export const buildSalarySlipPdfDocument = async (
  slip,
  company = readPayslipCompany(),
  { forEmail = false } = {}
) => {
  const resolvedCompany =
    company && typeof company.then === "function" ? await company : company;
  const letterhead = await loadLetterheadDataUrl(resolvedCompany, { forEmail });
  const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4", compress: forEmail });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const tableWidth = pageWidth - CONTENT_MARGIN_X * 2;
  const model = buildSlipViewModel(slip);

  drawLetterheadBackground(pdf, letterhead);

  let y = CONTENT_TOP;

  drawBorderedCell(pdf, CONTENT_MARGIN_X, y, tableWidth, PAYSLIP_HEADER_H + 6, {
    text: model.periodTitle,
    bold: true,
    fill: GRAY,
    align: "center",
    fontSize: 11,
    padding: PAYSLIP_CELL_PADDING,
  });
  y += PAYSLIP_HEADER_H + 6;

  const colWidths = getPayslipTableColWidths(tableWidth);

  y = drawInfoGrid(pdf, CONTENT_MARGIN_X, y, colWidths, [
    ["Employee Name", model.employeeName, "Total Working Days", model.totalWorkingDays],
    [model.employeeTypeLabel, model.employeeType, "Leaves Taken", model.leavesTaken],
    ["Designation", model.designation, "Paid Leave Utilized", model.paidLeaveUtilized],
    ["Date of Joining", model.dateOfJoining, "Paid Days", model.paidDays],
  ]);

  y = drawEarningsDeductionsTable(pdf, CONTENT_MARGIN_X, y, colWidths, model);
  y += 28;

  const maxSignatureY = pageHeight - CONTENT_BOTTOM_RESERVE - 70;
  drawSignature(pdf, CONTENT_MARGIN_X, Math.min(y, maxSignatureY), resolvedCompany);

  return { pdf, fileName: getSalarySlipFileName(slip) };
};

/** In-app PDF preview URL (caller must revoke when done). */
export const createSalarySlipPreviewUrl = async (slip, company = readPayslipCompany()) => {
  const { pdf, fileName } = await buildSalarySlipPdfDocument(slip, company);
  const blob = pdf.output("blob");
  const url = URL.createObjectURL(blob);
  return {
    url,
    fileName,
    revoke: () => URL.revokeObjectURL(url),
  };
};

/** Download salary slip PDF. */
export const downloadSalarySlipPdf = async (slip, company = readPayslipCompany()) => {
  const { pdf, fileName } = await buildSalarySlipPdfDocument(slip, company);
  pdf.save(fileName);
};

/** Build salary slip PDF blob for email (compressed to stay under SMTP attachment limit). */
export const buildSalarySlipPdfBlob = async (slip, company = readPayslipCompany()) => {
  const resolvedCompany =
    company && typeof company.then === "function" ? await company : company;
  const { pdf, fileName } = await buildSalarySlipPdfDocument(slip, resolvedCompany, { forEmail: true });
  const blob = pdf.output("blob");
  if (blob.size > MAX_SALARY_SLIP_EMAIL_BYTES) {
    throw new Error(
      "Salary slip PDF is too large to email (max 8MB). Try a smaller company signature image in payslip settings."
    );
  }
  return { blob, fileName };
};
